import { Component, OnInit, OnDestroy, Output, EventEmitter, Input } from '@angular/core';
import { Subject, takeUntil } from 'rxjs';
import { Router } from '@angular/router';
import { AyaConversationsService } from 'src/app/core/services/aya-conversations.service';
import { ChatConversation, ChatHistoryGroup } from 'src/app/core/models/chat-history.model';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-chat-history',
  templateUrl: './chat-history.component.html',
  styleUrls: ['./chat-history.component.scss']
})
export class ChatHistoryComponent implements OnInit, OnDestroy {
  @Output() conversationSelected = new EventEmitter<string>();
  @Input() showAllConversations: boolean;
  searchQuery: string = '';
  groupedConversations: ChatHistoryGroup[] = [];
  filteredConversations: ChatConversation[] = [];
  activeConversationId: string | null = null;
  isLoading: boolean = true;
  private destroy$ = new Subject<void>();

  /** Sidebar preview count — matches ask-aya-health Header sidebar. */
  readonly sidebarPreviewLimit = 10;

  constructor(
    private ayaConversationsService: AyaConversationsService,
    private router: Router
  ) { }

  ngOnInit(): void {
    this.ayaConversationsService.loading$
      .pipe(takeUntil(this.destroy$))
      .subscribe((loading) => {
        this.isLoading = loading;
      });

    this.ayaConversationsService.getGroupedConversations()
      .pipe(takeUntil(this.destroy$))
      .subscribe(groups => {
        this.groupedConversations = groups;
      });

    this.ayaConversationsService.getFilteredConversations()
      .pipe(takeUntil(this.destroy$))
      .subscribe(conversations => {
        this.filteredConversations = conversations;
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onSearchChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery = target.value;
    this.ayaConversationsService.searchConversations(this.searchQuery);
  }

  onConversationClick(conversationId: string): void {
    // Set the active conversation
    this.activeConversationId = conversationId;
    
    if (conversationId === 'all') {
      // Navigate to chats page with showAll=true query parameter
      this.router.navigate(['/chats'], { 
        queryParams: {
          conversationId: 'all',
          showAll: 'true'
        } 
      });
    } else if (conversationId) {
      this.conversationSelected.emit(conversationId);
      this.router.navigate(['/chats'], { queryParams: { conversationId } });
    }
  }

  formatTime(timestamp: Date): string {
    return new Date(timestamp).toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  }

  truncateText(text: string, maxLength: number = 50): string {
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
  }

  getLimitedConversations(conversations: ChatConversation[], limit: number): ChatConversation[] {
    return conversations.slice(0, limit);
  }

  // Flatten all grouped conversations into a single array
  getAllConversationsFlat(): ChatConversation[] {
    const allConversations: ChatConversation[] = [];
    this.groupedConversations.forEach(group => {
      allConversations.push(...group.conversations);
    });
    return allConversations;
  }

  // Get total count of all conversations
  getTotalConversationsCount(): number {
    return this.groupedConversations.reduce((total, group) => total + group.conversations.length, 0);
  }

  // Check if a conversation is currently active
  isActiveConversation(conversationId: string): boolean {
    return this.activeConversationId === conversationId && this.router.url.includes(conversationId);
  }

  // Delete conversation with confirmation
  deleteConversation(conversationId: string, event: Event): void {
    // Prevent the conversation click event from firing
    event.stopPropagation();
    
    // Show SweetAlert2 confirmation dialog
    Swal.fire({
      title: 'Are you sure you want to delete this conversation?',
      text: 'This action cannot be undone',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#9ca3af',
      confirmButtonText: 'Delete',
      cancelButtonText: 'Cancel'
    }).then((result) => {
      if (result.isConfirmed) {
        // User clicked "Delete"
        this.ayaConversationsService.deleteConversation(conversationId);
        
        // Show success message
        Swal.fire({
          title: 'Deleted!',
          text: 'Conversation has been deleted successfully',
          icon: 'success',
          timer: 2000,
          showConfirmButton: false
        });

        this.router.navigate(['/chats']);  
        
      }
      // If user clicked "Cancel", do nothing (just close the popup)
    });
  }
}
